/* Wrapped in an IIFE so its top-level names stay out of the global scope —
   theme scripts share one global scope, and a duplicated top-level const makes
   the whole file fail to parse. */
(() => {
  /**
   * <product-carousel> — prev/next buttons over a scroll-snap row.
   *
   * The browser still owns the scrolling; this only nudges it and reports where it
   * ended up, so swipe, momentum and snapping keep working untouched. Touch devices
   * already have the gesture, so the buttons are a pointer-only affordance and the
   * CSS hides them on narrow screens.
   */
  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.nav = this.querySelector('[data-carousel-nav]');
      if (!this.track || !this.nav) return;

      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');

      this.prev?.addEventListener('click', () => this.#step(-1));
      this.next?.addEventListener('click', () => this.#step(1));
      this.track.addEventListener('scroll', this.#sync, { passive: true });

      // Cards reflow with the viewport, so how far a click travels and whether the
      // row overflows at all are both width-dependent.
      this.observer = new ResizeObserver(this.#sync);
      this.observer.observe(this.track);

      this.#sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.track?.removeEventListener('scroll', this.#sync);
    }

    /* Moves a screenful at a time rather than a single card: with four cards in
       view, one-card steps turn browsing a collection into a drum roll. Snapping
       lands the result on a card edge either way. */
    #step(direction) {
      const stride = this.#stride;
      if (!stride) return;

      const perView = Math.max(1, Math.floor(this.track.clientWidth / stride));
      this.track.scrollBy({ left: stride * perView * direction, behavior: 'smooth' });
    }

    /* Distance from one card to the next, gap included. */
    get #stride() {
      const cards = [...this.track.children];
      if (cards.length === 0) return 0;

      const first = cards[0].getBoundingClientRect();
      return cards.length > 1
        ? cards[1].getBoundingClientRect().left - first.left
        : first.width;
    }

    /* How much scrolling is left before the last card sits flush with the right
       edge. Measured from the card rather than from scrollWidth, because the track
       bleeds a page margin of padding past the last card — counting that as
       scrollable would show the buttons on a row that has nothing left to reveal. */
    get #end() {
      const last = this.track.lastElementChild;
      if (!last) return 0;

      const overshoot = last.getBoundingClientRect().right - this.track.getBoundingClientRect().right;
      return Math.max(0, this.track.scrollLeft + overshoot);
    }

    #sync = () => {
      const end = this.#end;
      const at = this.track.scrollLeft;

      // Fractional layout widths mean neither end lands on a round number.
      this.nav.hidden = end <= 1;
      if (this.prev) this.prev.disabled = at <= 1;
      if (this.next) this.next.disabled = at >= end - 1;
    };
  }

  if (!customElements.get('product-carousel')) {
    customElements.define('product-carousel', ProductCarousel);
  }
})();
